import React, { useMemo } from "react";
import {
  Input,
  Row,
  Typography,
  Form,
  Col,
  Divider,
  Select,
  DatePicker,
} from "antd";
import { staticConfig, labelConfig } from "../../../glAndAccounting.config";
import appLabel from "../../../../../config/appLabel";
import { BOM } from "../../../../../services/definedMethod";

const { Title, Text } = Typography;

const OverviewForm = (props) => {
  const {
    onFinish,
    form,
    formData,
    glCodeLength,
    templateData,
    parentData,
    tagData,
    accountUsageData,
    statusData,
    accountTypeData,
  } = props;

  const openingDate = Form.useWatch("approvedOnDate", form);
  const selectedAccountType = Form.useWatch("type", form);

  // Derive filtered IFRS options based on selected account type
  const ifrsOptions = useMemo(() => {
    if (!selectedAccountType || !accountTypeData?.length) return [];
    const typeOption = accountTypeData.find(
      (o) => o.value === selectedAccountType,
    );
    if (!typeOption) return [];
    const categoryKey = typeOption.label.toUpperCase();
    const group = (templateData.ifrsClassificationOptions || []).find(
      (g) => g.category === categoryKey,
    );
    return (group?.options || []).map((o) => ({ value: o.id, label: o.value }));
  }, [
    selectedAccountType,
    accountTypeData,
    templateData.ifrsClassificationOptions,
  ]);

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      form={form}
      initialValues={formData}
    >
      <Divider orientation="left" orientationMargin="0">
        <Title level={5} style={{ margin: 0, marginBottom: 0 }}>
          {labelConfig.glOverview}
        </Title>
      </Divider>
      <Row>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="type"
            label={<Text strong>{appLabel.accountType}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(appLabel.accountType),
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(appLabel.accountType)}
              showSearch
              options={accountTypeData}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="glCode"
            label={<Text strong>{appLabel.GLCode}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(appLabel.GLCode),
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value.length < glCodeLength) {
                    return Promise.reject(
                      new Error(
                        `GL Code must be ${glCodeLength} characters long`,
                      ),
                    );
                  }

                  if (value.length > glCodeLength) {
                    return Promise.reject(
                      new Error(
                        `Value must not exceed ${glCodeLength} characters`,
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={BOM.handlePlaceholderMsg(appLabel.GLCode)} />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="name"
            label={<Text strong>{labelConfig.glName}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(labelConfig.glName),
              },
            ]}
          >
            <Input placeholder={BOM.handlePlaceholderMsg(labelConfig.glName)} />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="shortName"
            label={<Text strong>{appLabel.shortName}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(appLabel.shortName),
              },
            ]}
          >
            <Input placeholder={BOM.handlePlaceholderMsg(appLabel.shortName)} />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="usage"
            label={<Text strong>{appLabel.accountUsage}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(appLabel.accountUsage),
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(appLabel.accountUsage)}
              showSearch
              options={accountUsageData}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item name="tagId" label={<Text strong>{appLabel.tag}</Text>}>
            <Select
              placeholder={BOM.handlePlaceholderMsg(appLabel.tag)}
              showSearch
              options={tagData}
              key={selectedAccountType}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            label={<Text strong>{appLabel.manualEntriesAllowed}</Text>}
            name="manualEntriesAllowed"
          >
            <Select
              options={staticConfig.yesOrNo}
              placeholder={BOM.handlePlaceholderMsg(
                appLabel.manualEntriesAllowed,
              )}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="category"
            label={<Text strong>{appLabel.category}</Text>}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(appLabel.category)}
              showSearch
              options={templateData.categoryOptions}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
        </Col>
        {/* <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="accountLevel"
            label={<Text strong>{labelConfig.accountLevel}</Text>}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(labelConfig.accountLevel)}
              showSearch
              options={templateData.accountLevelOptions}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
        </Col> */}
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="parentId"
            label={<Text strong>{appLabel.parent}</Text>}
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(appLabel.parent)}
              showSearch
              options={parentData}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              key={selectedAccountType}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="accountStatus"
            label={<Text strong>{labelConfig.accountStatus}</Text>}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(labelConfig.accountStatus)}
              showSearch
              options={statusData}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="naturalBalance"
            label={<Text strong>{labelConfig.naturalBalance}</Text>}
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(labelConfig.naturalBalance)}
              showSearch
              options={templateData.naturalBalanceOptions}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              key={selectedAccountType}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="reportingCode"
            label={<Text strong>{labelConfig.regulatoryReportingCode}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(labelConfig.regulatoryReportingCode),
              },
            ]}
          >
            <Input
              placeholder={BOM.handlePlaceholderMsg(
                labelConfig.regulatoryReportingCode,
              )}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="ifrsClassification"
            label={<Text strong>{labelConfig.ifrsClassification}</Text>}
            rules={[
              {
                required: ifrsOptions.length > 0,
                message: BOM.CheckReqFld(labelConfig.ifrsClassification),
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(
                labelConfig.ifrsClassification,
              )}
              showSearch
              options={ifrsOptions}
              allowClear
              disabled={ifrsOptions.length === 0}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            name="taxCategory"
            label={<Text strong>{labelConfig.taxCategory}</Text>}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(labelConfig.taxCategory),
              },
            ]}
          >
            <Select
              placeholder={BOM.handlePlaceholderMsg(labelConfig.taxCategory)}
              showSearch
              options={templateData.taxCategoryOptions}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            label={<Text strong>{labelConfig.openingDate}</Text>}
            name={"openingDate"}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(labelConfig.openingDate),
              },
            ]}
          >
            <DatePicker style={{ width: "100%", height: 35 }} />
          </Form.Item>
        </Col>
        <Col span={8} style={{ padding: "0px 8px" }}>
          <Form.Item
            label={<Text strong>{labelConfig.closingDate}</Text>}
            name={"closingDate"}
            rules={[
              {
                required: true,
                message: BOM.CheckReqFld(labelConfig.closingDate),
              },
            ]}
          >
            <DatePicker
              disabledDate={(current) =>
                openingDate && current && current.isBefore(openingDate, "day")
              }
              style={{ width: "100%", height: 35 }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default OverviewForm;
